import { useEffect } from 'react';
import { StyleSheet, View } from 'react-native';
import type { StackNavigationOptions } from '@react-navigation/stack';
import { createTheme as createDSTheme } from 'app/shopify';
import type { ScreenOptionsNavigationProp, SpecificRouteProp } from 'app/types/navigation';
import { createScreenOptions } from '../screen-options';
import { MODAL_WIDTH } from '../dimensions';
import { HeaderLeft } from './web-header-left';
import { HeaderTitle } from './web-header-title';
import { isMobileNative } from 'app/utils/constants';
import { getWebWindow } from 'app/utils/web-window';

const theme = createDSTheme();

function WebModalOverlay({ navigation }: { navigation: ScreenOptionsNavigationProp }) {
  const window = getWebWindow();
  useEffect(() => {
    if (isMobileNative || !window || typeof document === 'undefined') return;
    const onPointerDown = (event: PointerEvent) => {
      if (!window) return;
      if (!navigation.isFocused()) return;

      // Sheets use a React Native Modal portal, including their backdrop. Let
      // that modal handle the interaction without dismissing the screen below.
      if (event.target instanceof Element && event.target.closest('[aria-modal="true"]')) return;

      const width = Math.min(MODAL_WIDTH, window.innerWidth);
      const height = window.innerHeight > 960 ? 960 : window.innerHeight * 0.9;
      const left = (window.innerWidth - width) / 2;
      const right = left + width;
      const top = (window.innerHeight - height) / 2;
      const bottom = top + height;

      if (event.clientX < left || event.clientX > right || event.clientY < top || event.clientY > bottom) {
        navigation.goBack();
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [navigation, window]);

  return <View pointerEvents="none" style={[StyleSheet.absoluteFill, { backgroundColor: '#000', opacity: 0.25 }]} />;
}

export function createWebModalScreenOptions(
  navigation: ScreenOptionsNavigationProp,
  route: SpecificRouteProp,
  headerShown?: boolean,
): StackNavigationOptions {
  const screenOpts = createScreenOptions(route) as StackNavigationOptions;

  const canGoBack = navigation.getState().index > 0 && navigation.getState().routes[0]?.name !== 'main';
  const baseOptions: StackNavigationOptions = {
    headerBackButtonDisplayMode: 'minimal',
    headerBackTitleStyle: { color: theme.colors['white-label.header.on-header'] },
    headerTintColor: theme.colors['white-label.header.on-header'],
    headerStyle: {
      backgroundColor: theme.colors['white-label.header.bg'],
    },
    headerTitle: (_) => <HeaderTitle route={route} />,
    headerLeft: (_) => <HeaderLeft navigation={navigation} canGoBack={canGoBack} />,
  };

  const deviceHeight = getWebWindow()?.innerHeight ?? 0;

  return {
    ...screenOpts,
    header: undefined,
    headerShown: headerShown !== false,
    headerTitleAlign: 'left',
    ...baseOptions,
    presentation: 'transparentModal' as const,
    cardOverlayEnabled: true,
    cardStyle: {
      width: MODAL_WIDTH,
      alignSelf: 'center' as const,
      backgroundColor: theme.colors.page,
      borderRadius: theme.borderRadii['radius.xl'],
      overflow: 'hidden' as const,
      flex: 1,
      minHeight: deviceHeight > 960 ? 960 : ('90%' as const),
      margin: 'auto',
      maxHeight: deviceHeight > 960 ? 960 : ('90%' as const),
    },
    cardStyleInterpolator: ({ current: { progress } }) => ({
      cardStyle: {
        opacity: 1,
      },
      overlayStyle: {
        opacity: progress.interpolate({
          inputRange: [0, 1],
          outputRange: [0.25, 0.25],
        }),
      },
    }),
    cardOverlay: () => <WebModalOverlay navigation={navigation} />,
  };
}
